import { BandFollowButton } from "@/components/BandFollowButton";
import { BandFriend } from "@/components/BandFriend";
import { BandMembers } from "@/components/BandMembers";
import { ContentContainer } from "@/components/ContentContainer";
import { DialogStyled } from "@/components/DialogStyled";
import { EventOverview } from "@/components/EventOverview";
import { LinkCollection } from "@/components/LinkCollection";
import { useConfirm } from "@/components/material-ui-confirm";
import { PlaylistSmall } from "@/components/PlaylistSmall";
import { Spinner } from "@/components/Spinner";
import AppContext from "@/context/AppContext";
import { nextTrackState, playerPlaylistState } from "@/context/states";
import {
  getBandBySlug,
  getPublicBands,
  reportBand,
  useBand,
  useCreatedBandFriendships,
  useReceivedBandFriendships,
} from "@/lib/api";
import { nl2br } from "@/lib/helper";
import FourOhFour from "@/pages/404";
import theme from "@/styles/theme";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import DownloadIcon from "@mui/icons-material/Download";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import { Skeleton } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { grey } from "@mui/material/colors";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useContext, useEffect, useMemo, useState } from "react";
import { useRecoilState } from "recoil";

const DialogCanLive = dynamic(
  () => import("@/components/music/DialogCanLive"),
  { loading: () => <Skeleton /> },
);

const infobox = {
  "&.infobox": {
    p: 2,
    mt: 1,
    backgroundColor: grey[200],
    [theme.breakpoints.up("sm")]: {
      p: 3,
    },
    "&--links": {
      textAlign: "center",
      textTransform: "uppercase",
      color: theme.palette.secondary.main,
    },
    "&--downloads": {
      textAlign: "center",
      textTransform: "uppercase",
      color: theme.palette.secondary.main,
    },
  },
  "&.report": {
    "& .MuiButton-root": {
      textAlign: "right",
      textTransform: "uppercase",
      color: grey[400],
      padding: 0,
      fontSize: ".875rem",
    },
  },
};

const Band = ({ band, page }) => {
  const router = useRouter();
  const params = router.query.params ?? [];
  const { setSnackbar, band: loggedInBand } = useContext(AppContext);
  const [nextTrack, setNextTrack] = useRecoilState(nextTrackState);
  const [playerPlaylist, setPlayerPlaylist] =
    useRecoilState(playerPlaylistState);
  const { band: bandData, isLoading, isError } = useBand(params[0]);
  const {
    bandFriendships: receivedFriendships,
    isLoading: loadingReceivedFriendships,
  } = useReceivedBandFriendships(band);
  const {
    bandFriendships: createdFriendships,
    isLoading: loadingCreatedFriendships,
  } = useCreatedBandFriendships(band);
  const confirm = useConfirm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPageGenerating, setIsPageGenerating] = useState(false);
  const handleOpen = () => setIsModalOpen(true);
  const handleModalClosed = () => setIsModalOpen(false);

  const friendships = useMemo(() => {
    if (!receivedFriendships || !createdFriendships) {
      return null;
    }
    let newFriendships = [];
    if (receivedFriendships && receivedFriendships.length > 0) {
      for (const f of receivedFriendships.filter(
        (ef) => ef.confirmed != null,
      )) {
        if (!newFriendships.find((ef) => ef.id === f.band.id)) {
          newFriendships = [...newFriendships, f.band];
        }
      }
    }
    if (createdFriendships && createdFriendships.length > 0) {
      for (const f of createdFriendships.filter((ef) => ef.confirmed != null)) {
        if (!newFriendships.find((ef) => ef.id === f.followed_band.id)) {
          newFriendships = [...newFriendships, f.followed_band];
        }
      }
    }

    return newFriendships;
  }, [
    loadingReceivedFriendships,
    loadingCreatedFriendships,
    receivedFriendships,
    createdFriendships,
  ]);

  useEffect(() => {
    if (params.length === 2 && !isLoading) {
      // start song, when there are 2 param
      if (params[1] === "play") {
        setPlayerPlaylist([...bandData.songs]);
        setNextTrack(bandData.songs[0]);
      } else {
        for (const song of bandData.songs) {
          if (song.id + "" === params[1] + "") {
            setPlayerPlaylist([song]);
            setNextTrack(song);
            break;
          }
        }
      }
    }
  }, [params, isLoading, bandData]);

  if (!band) {
    return <FourOhFour />;
  }
  const notifyAdmin = async () => {
    confirm({
      title: "Band melden",
      description: `Möchtest Du Dieses Profil wirklich zur Überprüfung an einen Administrator senden?`,
      confirmationText: `Ja, Profil melden.`,
    })
      .then(async () => {
        reportBand(band.id);
        setSnackbar("Profil zur Überprüfung gemeldet.");
      })
      .catch((error) => {});
  };

  const expireCache = async (event) => {
    event.preventDefault();

    setIsPageGenerating(true);
    await fetch(`https://www.showcase.nrw/api/cache/band?slug=${band.slug}`);

    router.reload();
  };

  return (
    <>
      <ContentContainer
        headline={band?.name}
        subheadline={`${
          band?.genre_description || bandData?.genres[0]?.title
        } aus ${band.zipcode?.label}`}
        bandImage={band.photo?.formats}
        image={page?.hero_image?.formats?.large?.url}
      >
        <Grid container>
          <Grid item xs={12}>
            <Grid container spacing={3}>
              <Grid
                item
                xs={12}
                md={6}
                lg={7}
                xl={8}
                sx={{
                  "& p.MuiTypography-root": {
                    mb: 2,
                  },
                }}
              >
                <Typography variant="h4" sx={{ mb: 1 }}>
                  Biografie
                </Typography>

                <Typography variant="body1" sx={{ wordWrap: "break-word" }}>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: nl2br(band.description),
                    }}
                  />
                </Typography>
                {band.credo && (
                  <>
                    <Typography variant="h5" sx={{ marginBottom: 0 }}>
                      Credo
                    </Typography>
                    <Typography variant="body1">{band.credo}</Typography>
                  </>
                )}
                {band.year_established && (
                  <>
                    <Typography variant="h5" sx={{ marginBottom: 0 }}>
                      Gründungsjahr
                    </Typography>
                    <Typography>{band.year_established}</Typography>
                  </>
                )}
                {band.average_year_of_birth && (
                  <>
                    <Typography variant="h5" sx={{ marginBottom: 0 }}>
                      Durchschnittlicher Jahrgang
                    </Typography>
                    <Typography>{band.average_year_of_birth}</Typography>
                  </>
                )}
                {band.best_gig && (
                  <>
                    <Typography variant="h5" sx={{ marginBottom: 0 }}>
                      Bester Gig bisher
                    </Typography>
                    <Typography variant="body1">{band.best_gig}</Typography>
                  </>
                )}
                {band.favourite_location && (
                  <>
                    <Typography variant="h5" sx={{ marginBottom: 0 }}>
                      Lieblings Location
                    </Typography>
                    <Typography>{band.favourite_location}</Typography>
                  </>
                )}
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                lg={5}
                xl={4}
                sx={{ "& > .MuiGrid-root": { mb: 4 } }}
              >
                <Grid container rowSpacing={1}>
                  {band.can_live && (
                    <Grid item xs={12}>
                      <Button
                        onClick={handleOpen}
                        color="secondary"
                        variant="contained"
                        fullWidth
                        startIcon={<EventAvailableIcon />}
                      >
                        <Box
                          component="span"
                          sx={{
                            display: { sm: "none" },
                          }}
                        >
                          Für Konzert anfragen
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            display: { xs: "none", sm: "inline-flex" },
                          }}
                        >
                          Band für Konzert anfragen
                        </Box>
                      </Button>
                      <DialogStyled
                        open={isModalOpen}
                        onClose={handleModalClosed}
                        cancelButton={true}
                      >
                        <DialogCanLive band={band} onSend={handleModalClosed} />
                      </DialogStyled>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <BandFollowButton
                      label={"Freundschaftsanfrage"}
                      labelsm={"Freundschaftsanfrage senden"}
                      band={bandData}
                    />
                  </Grid>
                  {band.allow_messages && band.verified && (
                    <Grid item xs={12}>
                      <Button
                        color="secondary"
                        variant="contained"
                        fullWidth
                        startIcon={<ChatBubbleIcon />}
                        onClick={() => {
                          router.push(`/secure/chat/new?band=${params[0]}`);
                        }}
                      >
                        Nachricht schreiben
                      </Button>
                    </Grid>
                  )}
                </Grid>
                {(bandData ?? band).songs?.filter(
                  (s) => s.soundcloud_link !== null,
                ).length > 0 && (
                  <Grid container>
                    <Grid item xs={12}>
                      <PlaylistSmall songs={(bandData ?? band).songs} />
                    </Grid>
                  </Grid>
                )}
                {band?.members.length >= 1 && (
                  <Grid container>
                    <Grid item xs={12}>
                      <Typography variant="h4">Besetzung</Typography>
                      <Box sx={infobox} className="infobox">
                        <BandMembers members={bandData?.members} />
                      </Box>
                    </Grid>
                  </Grid>
                )}
                <Grid container>
                  <Grid item xs={12}>
                    <Typography variant="h4">Links</Typography>
                    <Box sx={infobox} className="infobox infobox--links">
                      <LinkCollection band={band} />
                    </Box>
                  </Grid>
                </Grid>
                {(band.presskit || band.techrider) && (
                  <Grid container>
                    <Grid item xs={12}>
                      <Typography variant="h4">Downloads</Typography>
                      <Box sx={infobox} className="infobox infobox--downloads">
                        <Grid container>
                          {band.presskit && (
                            <Grid item xs={12}>
                              <a
                                href={band.presskit.url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <Button
                                  color="secondary"
                                  fullWidth
                                  startIcon={<DownloadIcon />}
                                >
                                  Pressekit
                                </Button>
                              </a>
                            </Grid>
                          )}
                          {band.techrider && (
                            <Grid item xs={12}>
                              <a
                                href={band.techrider.url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <Button
                                  color="secondary"
                                  fullWidth
                                  startIcon={<DownloadIcon />}
                                >
                                  Tech Rider
                                </Button>
                              </a>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    </Grid>
                  </Grid>
                )}
                <Grid container sx={infobox} className="report">
                  <Grid item xs={12}>
                    <Grid container justifyContent="flex-end">
                      <Grid item xs="auto">
                        <Button disableRipple onClick={notifyAdmin}>
                          Profil melden
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </ContentContainer>

      <Container
        maxWidth="xl"
        sx={{
          mt: -3,
          [theme.breakpoints.up("sm")]: {
            mt: -17,
          },
          "& h3.MuiTypography-root, h5.MuiTypography-root, h6.MuiTypography-root":
            {
              color: theme.palette.common.black,
              textAlign: "left",
              [theme.breakpoints.up("sm")]: {
                textAlign: "center",
                mb: 3,
              },
            },
        }}
      >
        <Grid container>
          <Grid
            item
            xs={12}
            sx={{
              bgcolor: theme.palette.common.white,
              [theme.breakpoints.up("sm")]: {
                bgcolor: grey[200],
                py: 6,
              },
            }}
          >
            <Grid container justifyContent="center">
              <Grid item xs={10}>
                <Typography
                  variant="h3"
                  sx={{
                    display: { xs: "block", sm: "none" },
                  }}
                >
                  Live
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    display: { xs: "none", sm: "block" },
                  }}
                >
                  {band.name} Live
                </Typography>
                <EventOverview
                  events={(bandData ?? band)?.events?.filter(
                    (e) => new Date(e.date_start) >= Date.now(),
                  )}
                />
              </Grid>
              {/*
              <Grid item xs={12} md={10} sx={{ display: "none",}}>
                <Grid container justifyContent="center">
                  <Grid item xs={10} md="auto">
                    <Link href="/events" passHref>
                      <Button
                        variant="outlined-secondary"
                        sx={{
                          width: "100%",
                          [theme.breakpoints.up("sm")]: {
                            mt: 4,
                            width: "auto",
                          },
                        }}
                      >
                        Zum Gig Archiv
                      </Button>
                    </Link>
                  </Grid>
                </Grid>
              </Grid>
              */}
            </Grid>
          </Grid>
        </Grid>
      </Container>
      <Container
        maxWidth="xl"
        sx={{
          mb: 3,
          [theme.breakpoints.up("sm")]: {
            mb: 6,
          },
          "& h3.MuiTypography-root, h5.MuiTypography-root, h6.MuiTypography-root":
            {
              color: theme.palette.common.black,
              textAlign: "left",
              py: 3,
              [theme.breakpoints.up("sm")]: {
                textAlign: "center",
                mb: 3,
              },
            },
        }}
      >
        <Grid container>
          <Grid
            item
            xs={12}
            sx={{
              pb: 3,
              bgcolor: theme.palette.common.white,
              [theme.breakpoints.up("sm")]: {
                pt: 6,
                pb: 13,
              },
            }}
          >
            {friendships.length > 0 && (
              <Grid container justifyContent="center" spacing={3}>
                <Grid item xs={10}>
                  <Typography
                    variant="h3"
                    sx={{
                      display: { xs: "block", sm: "none" },
                    }}
                  >
                    befreundet mit
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      display: { xs: "none", sm: "block" },
                    }}
                  >
                    {band.name} ist befreundet mit
                  </Typography>
                </Grid>
                {friendships.map((friend, idx) => (
                  <Grid item xs={10} sm={6} md={3} key={idx}>
                    <BandFriend band={friend} />
                  </Grid>
                ))}
              </Grid>
            )}
          </Grid>
          {loggedInBand && loggedInBand.id + "" === band.id + "" && (
            <Grid item xs={12}>
              <Paper sx={{ mt: 4, p: 4, textAlign: "center" }}>
                <Typography sx={{ mb: 1 }}>
                  Diese Seite wird alle 15 Minuten generiert.
                  <br /> Klick auf den Button, falls Du das abkürzen willst.
                </Typography>
                <Button
                  disabled={isPageGenerating}
                  onClick={expireCache}
                  variant="contained"
                  color="secondary"
                >
                  {isPageGenerating ? <Spinner /> : <>Jetzt neu generieren</>}
                </Button>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Container>
    </>
  );
};

export async function getStaticProps(context) {
  const slug = context.params.params[0];

  const baseUrl = `${
    process.env.NEXT_PUBLIC_API_URL || "http://localhost:1337"
  }`;

  const bandLoader = getBandBySlug(slug);
  const pageLoader = fetch(`${baseUrl}/page-music-find`);

  const [band, pageResponse] = await Promise.all([bandLoader, pageLoader]);
  const page = await pageResponse.json();

  return {
    props: {
      band: band?.length > 0 ? band[0] : null,
      page,
    },
    revalidate: parseInt(process.env.SSG_REVALIDATE_BANDS),
  };
}

export async function getStaticPaths() {
  const bands = await getPublicBands();

  // Get the paths we want to pre-render based on posts
  const paths = bands.map((band) => ({
    params: { params: [band.slug] },
  }));

  // We'll pre-render only these paths at build time.
  // { fallback: 'blocking' } will server-render pages
  // on-demand if the path doesn't exist.
  return { paths, fallback: "blocking" };
}

export default Band;
